import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Input, DatePicker, message, Space, Popconfirm } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { getActivations, deleteActivation } from '../services/api';
import moment from 'moment';

const { RangePicker } = DatePicker;

const ActivationList = () => {
    const [activations, setActivations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [searchParams, setSearchParams] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const fetchActivations = async (page = 1, pageSize = 10, params = {}) => {
        setLoading(true);
        try {
            const response = await getActivations({ page, per_page: pageSize, ...params });
            setActivations(response.data.activations);
            setPagination({ current: page, pageSize, total: response.data.total });
        } catch (error) {
            message.error('获取激活记录失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchActivations(pagination.current, pagination.pageSize, searchParams);
    }, []); // 依赖项为空，仅在组件挂载时运行

    const handleTableChange = (newPagination) => {
        fetchActivations(newPagination.current, newPagination.pageSize, searchParams);
    };

    const handleSearch = (values) => {
        const { activated_at_range, ...rest } = values;
        const params = {
            ...rest,
            activated_at_start: activated_at_range && activated_at_range.length > 0 ? activated_at_range[0].format('YYYY-MM-DD') : '',
            activated_at_end: activated_at_range && activated_at_range.length > 1 ? activated_at_range[1].format('YYYY-MM-DD') : '',
        };
        setSearchParams(params);
        fetchActivations(1, pagination.pageSize, params);
    };

    const handleDelete = async (ids) => {
        if (!ids || ids.length === 0) {
            message.warning('请选择要删除的条目');
            return;
        }
        try {
            await deleteActivation({ activation_ids: ids });
            message.success(`成功删除 ${ids.length} 个激活记录`);
            setSelectedRowKeys([]);
            // 重新获取数据
            // 考虑如果当前页在删除后变为空的情况
            const itemsOnCurrentPageAfterDeletion = activations.filter(act => !ids.includes(act.id)).length;
            if (itemsOnCurrentPageAfterDeletion === 0 && pagination.current > 1 && ids.length === activations.length) {
                // 如果删除了当前页的所有数据且不是第一页，则请求前一页
                fetchActivations(pagination.current - 1, pagination.pageSize, searchParams);
            } else {
                fetchActivations(pagination.current, pagination.pageSize, searchParams);
            }
        } catch (error) {
            message.error('删除失败');
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: '许可证密钥', dataIndex: 'license_key', key: 'license_key' },
        { title: '产品标识', dataIndex: 'product_identifier', key: 'product_identifier' },
        { title: '硬件ID', dataIndex: 'hardware_id', key: 'hardware_id' },
        {
            title: '激活时间',
            dataIndex: 'activated_at',
            key: 'activated_at',
            render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-'
        },
        { title: 'IP地址', dataIndex: 'ip_address', key: 'ip_address' },
        { title: 'User-Agent', dataIndex: 'user_agent', key: 'user_agent' },
        { title: '许可证备注', dataIndex: 'license_notes', key: 'license_notes' },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title={`确定删除这条激活记录 (ID: ${record.id}) 吗?`}
                    onConfirm={() => handleDelete([record.id])}
                    okText="确定"
                    cancelText="取消"
                >
                    <Button icon={<DeleteOutlined />} danger>
                        {/* 可以选择不显示文字，只显示图标，或保留“删除”文字 */}
                        {/* 删除 */}
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const hasSelected = selectedRowKeys.length > 0;

    return (
        <>
            <Form layout="inline" onFinish={handleSearch} style={{ marginBottom: 16 }}>
                <Form.Item name="license_key" label="许可证密钥">
                    <Input placeholder="输入许可证密钥" allowClear />
                </Form.Item>
                <Form.Item name="hardware_id" label="硬件ID">
                    <Input placeholder="输入硬件ID" allowClear />
                </Form.Item>
                <Form.Item name="product_identifier" label="产品标识">
                    <Input placeholder="输入产品标识" allowClear />
                </Form.Item>
                <Form.Item name="ip_address" label="IP地址">
                    <Input placeholder="输入IP地址" allowClear />
                </Form.Item>
                <Form.Item name="user_agent" label="User-Agent">
                    <Input placeholder="输入User-Agent" allowClear />
                </Form.Item>
                <Form.Item name="license_notes" label="许可证备注">
                    <Input placeholder="输入许可证备注" allowClear />
                </Form.Item>
                <Form.Item name="activated_at_range" label="激活时间">
                    <RangePicker />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        搜索
                    </Button>
                </Form.Item>
            </Form>

            <Space style={{ marginBottom: 16 }}>
                {hasSelected && (
                    <Popconfirm
                        title={`确定删除选中的 ${selectedRowKeys.length} 个激活记录吗?`}
                        onConfirm={() => handleDelete(selectedRowKeys)}
                        okText="确定"
                        cancelText="取消"
                        disabled={!hasSelected}
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            disabled={!hasSelected}
                        >
                            批量删除 ({selectedRowKeys.length})
                        </Button>
                    </Popconfirm>
                )}
            </Space>

            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={activations}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }} // 添加水平滚动，以防内容过多
            />
        </>
    );
};

export default ActivationList;
